/**
 * "Priority — needs your attention".
 *
 * The manager's single "what must I deal with right now" list, fed by the
 * dashboard status foundation. Whole rows are clickable and open the source
 * item by type. Headers on desktop, cards on mobile; coloured left-edge by band.
 */

import { Avatar } from "@package/subframe/components/Avatar";
import { Badge } from "@package/subframe/components/Badge";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { FeatherArrowRight } from "@subframe/core";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import type { PriorityLink, PriorityRow } from "@/lib/dashboard/priority-list";
import {
  DASHBOARD_STATUS_META,
  type DashboardStatus,
} from "@/lib/dashboard/traffic-light";

import { OverviewCard, SectionHeader } from "./_shared";
import { NaudBadgeIcon, toneFromStatus } from "./NaudIcon";

const SEVERITY_EDGE: Record<PriorityRow["band"], string> = {
  ACTION: "border-l-error-500",
  NEEDS_ATTENTION: "border-l-warning-500",
};

/** How many rows show before "show more" expands the rest. */
const COLLAPSED_ROWS = 5;

/**
 * Header and rows must share this, or the labels drift off their columns.
 * 890 = 684 fixed columns/gaps/padding + 52 icon + 148 badge; below that the
 * Item column starves and the badge overflows it (NAU-382). Rows with an `area`
 * label share that line, so those still ellipsise. Mobile keeps 760 — it hides
 * two columns and must not gain extra scroll.
 */
const ROW_MIN_WIDTH = "min-w-[890px] mobile:min-w-[760px]";

/**
 * Lets a Badge ellipsise rather than escape its cell. `overflow-hidden` is what
 * makes it shrinkable — a flex item's automatic minimum is its content width
 * until overflow stops being visible; the ellipsis must sit on Badge's inner
 * span, which is `whitespace-nowrap`.
 */
const SHRINKABLE_BADGE =
  "min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:overflow-hidden [&>span]:text-ellipsis";

function AssigneeCell({ row }: { row: PriorityRow }) {
  if (!row.assignee) {
    return (
      <span className="text-caption font-caption text-subtext-color">—</span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {row.assigneeInitials && (
        <Avatar size="small" variant="neutral">
          {row.assigneeInitials}
        </Avatar>
      )}
      <span className="truncate text-caption font-caption text-neutral-700">
        {row.assignee}
      </span>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: COLLAPSED_ROWS }).map((_, i) => (
        <div
          key={i}
          className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? "border-t border-t-neutral-border" : ""}`}
        >
          <div className="h-9 w-9 flex-none animate-pulse rounded-full bg-neutral-100" />
          <div className="flex grow basis-0 flex-col gap-1">
            <div className="h-3 w-1/3 animate-pulse rounded bg-neutral-100" />
            <div className="h-2 w-1/4 animate-pulse rounded bg-neutral-100" />
          </div>
          <div className="h-5 w-20 flex-none animate-pulse rounded bg-neutral-100" />
        </div>
      ))}
    </div>
  );
}

interface PriorityListProps {
  rows: PriorityRow[];
  loading?: boolean;
  onOpen: (link: PriorityLink) => void;
  onViewAll: () => void;
}

export function PriorityList({
  rows,
  loading,
  onOpen,
  onViewAll,
}: PriorityListProps) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  const hiddenCount = rows.length - visible.length;

  return (
    <OverviewCard className="w-full">
      <div className="p-5 pb-2">
        <SectionHeader
          title="Priority — needs your attention"
          count={loading ? undefined : rows.length}
        />
      </div>

      {loading ? (
        <SkeletonRows />
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center px-5 py-10">
          <span className="text-body font-body text-subtext-color">
            Nothing needs your attention right now. Nicely done.
          </span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Header (desktop only) */}
          <div
            className={`flex items-center gap-4 px-5 py-2 mobile:hidden ${ROW_MIN_WIDTH}`}
          >
            <span className="grow basis-0 text-caption-bold font-caption-bold text-neutral-500">
              Item
            </span>
            <span className="w-28 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Type
            </span>
            <span className="w-40 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Assigned to
            </span>
            <span className="w-44 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Due date / Issue
            </span>
            <span className="w-32 flex-none text-caption-bold font-caption-bold text-neutral-500">
              Status
            </span>
          </div>

          {visible.map((row, index) => {
            const meta = DASHBOARD_STATUS_META[row.band as DashboardStatus];
            return (
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(row.link)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(row.link);
                  }
                }}
                className={`flex cursor-pointer items-center gap-4 border-l-4 border-solid ${SEVERITY_EDGE[row.band]} px-5 py-3 hover:bg-neutral-50 ${index > 0 ? "border-t border-t-neutral-border" : ""} mobile:flex-wrap ${ROW_MIN_WIDTH}`}
              >
                {/* Item */}
                <div className="flex grow basis-0 min-w-0 items-center gap-3">
                  <NaudBadgeIcon
                    icon={AlertTriangle}
                    tone={toneFromStatus(row.band)}
                    size="lg"
                  />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-body-bold font-body-bold text-primary-navy-800">
                      {row.title}
                    </span>
                    {(row.area ||
                      row.awaitingSignOff ||
                      row.needsAcknowledgement) && (
                      <div className="flex min-w-0 items-center gap-2">
                        {row.area && (
                          <span className="truncate text-caption font-caption text-subtext-color">
                            {row.area}
                          </span>
                        )}
                        {row.awaitingSignOff && (
                          <Badge
                            className={SHRINKABLE_BADGE}
                            title="Awaiting sign-off"
                            variant="brand"
                          >
                            Awaiting sign-off
                          </Badge>
                        )}
                        {row.needsAcknowledgement && (
                          <Badge
                            className={SHRINKABLE_BADGE}
                            title="Needs acknowledgement"
                            variant="error"
                          >
                            Needs acknowledgement
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Type */}
                <div className="w-28 flex-none mobile:hidden">
                  <Badge className="w-full justify-center" variant="neutral">
                    {row.typeLabel}
                  </Badge>
                </div>

                {/* Assigned to */}
                <div className="w-40 flex-none mobile:hidden">
                  <AssigneeCell row={row} />
                </div>

                {/* Due date / triggering detail */}
                <div className="flex w-44 flex-none flex-col">
                  <span
                    className={`text-caption font-caption ${row.overdue ? "text-error-700 font-caption-bold" : "text-neutral-700"}`}
                  >
                    {row.dueLabel}
                  </span>
                  {row.dueSubLabel && (
                    <span
                      className={`text-caption font-caption ${row.overdue ? "text-error-600" : "text-subtext-color"}`}
                    >
                      {row.dueSubLabel}
                    </span>
                  )}
                </div>

                {/* Status (band) */}
                <div className="w-32 flex-none">
                  <Badge
                    className="w-full justify-center"
                    variant={meta.badgeVariant}
                  >
                    {meta.label}
                  </Badge>
                </div>
              </div>
            );
          })}

          {hiddenCount > 0 && (
            <div className="border-t border-solid border-neutral-border px-5 py-2">
              <LinkButton
                variant="neutral"
                size="small"
                onClick={() => setExpanded(true)}
              >
                {`Show ${hiddenCount} more`}
              </LinkButton>
            </div>
          )}
          {expanded && rows.length > COLLAPSED_ROWS && (
            <div className="border-t border-solid border-neutral-border px-5 py-2">
              <LinkButton
                variant="neutral"
                size="small"
                onClick={() => setExpanded(false)}
              >
                Show less
              </LinkButton>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end border-t border-solid border-neutral-border bg-secondary-beige-50 px-5 py-3">
        <LinkButton
          variant="brand"
          size="small"
          iconRight={<FeatherArrowRight />}
          onClick={onViewAll}
        >
          View all follow-up actions
        </LinkButton>
      </div>
    </OverviewCard>
  );
}
